import apiClient from "@/api-client";
import { useMutation, useQuery } from "@tanstack/react-query";

import { logger } from "@/lib/default-logger";
import { toast } from "@/components/core/toaster";

import type { FileMapping } from "./use-contacts";
import { oauthErrorMessage } from "./use-oauth";

export interface ZoomInfoAudience {
	id: string;
	name: string;
	recordCount: number;
	/** False above the import's row cap; an older API leaves it out. */
	importable?: boolean;
}

/** An import session like a CSV upload's, plus what the Do Not Call check did. */
export interface ZoomInfoImport extends FileMapping {
	/** Numbers ZoomInfo marked Do Not Call, now on the suppression list. */
	doNotCall: number;
	/** Contacts left out because ZoomInfo could not confirm they may be called. */
	excluded: number;
}

export const zoominfoKeys = {
	all: ["zoominfo"] as const,
	audiences: (pluginId: string) => [...zoominfoKeys.all, "audiences", pluginId] as const,
};

export function useZoomInfoAudiences(pluginId: string, enabled = true) {
	return useQuery({
		queryKey: zoominfoKeys.audiences(pluginId),
		queryFn: async (): Promise<ZoomInfoAudience[]> => {
			const response = await apiClient.get(`/plugins/${pluginId}/zoominfo/audiences`);
			return response.data;
		},
		enabled: !!pluginId && enabled,
	});
}

export function useImportZoomInfoAudience() {
	return useMutation({
		mutationFn: async ({ pluginId, audienceId }: { pluginId: string; audienceId: string }): Promise<ZoomInfoImport> => {
			const response = await apiClient.post(`/plugins/${pluginId}/zoominfo/audiences/${audienceId}/import`);
			return response.data;
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error(oauthErrorMessage(error));
		},
	});
}
